import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { CrmEntry } from "@domain/crm/crm";
import { environment } from "@environments/environment";
import { plainToInstance } from "class-transformer";
import { map, Observable } from "rxjs";
import { CrmEntryDto } from "../../navinta-lib/dtos/crm";

@Injectable({
  providedIn: "root",
})
export class CrmService {
  private httpClient = inject(HttpClient);

  public static readonly CRM_URL = environment.apiUrl + "/crm";

  findById(params: { contactId?: string; opportunityId?: string }): Observable<CrmEntry> {
    const queryParams: { crmContactId?: string; crmOpportunityId?: string } = {};
    if (params.contactId) queryParams.crmContactId = params.contactId;
    if (params.opportunityId) queryParams.crmOpportunityId = params.opportunityId;

    return this.httpClient.get(CrmService.CRM_URL, { params: queryParams }).pipe(
      map((response) => {
        const responseData = response as Record<string, unknown> | null;
        if (responseData && this.hasMappableData(responseData)) {
          const crmEntryDto = plainToInstance(CrmEntryDto, responseData, { excludeExtraneousValues: true });
          const crmEntry = plainToInstance(CrmEntry, crmEntryDto);
          crmEntry.sourceType = params.opportunityId ? "opportunity" : "contact";
          crmEntry.id = params.opportunityId ?? params.contactId;
          crmEntry.opportunityId = this.normalizeOptionalString(crmEntry.opportunityId) ?? params.opportunityId;
          return crmEntry;
        }
        throw new Error("No data for crm identifier");
      }),
    );
  }

  private hasMappableData(response: Record<string, unknown>): boolean {
    const hasRootValue =
      this.hasText(response["name"]) ||
      this.hasText(response["opportunityid"]) ||
      this.hasText(response["opportunityId"]) ||
      this.hasText(response["opportunityLink"]);

    const hasCustomerValue =
      this.hasAnyNonEmptyValue(response["customer_shippedTo"]) ||
      this.hasAnyNonEmptyValue(response["customerShippedTo"]) ||
      this.hasAnyNonEmptyValue(response["company"]) ||
      this.hasAnyNonEmptyValue(response["customer_soldTo"]) ||
      this.hasAnyNonEmptyValue(response["customerSoldTo"]) ||
      this.hasAnyNonEmptyValue(response["endUser"]);

    const hasContactValue = this.hasAnyNonEmptyValue(response["contact"]);

    return hasRootValue || hasCustomerValue || hasContactValue;
  }

  private hasAnyNonEmptyValue(value: unknown): boolean {
    if (!value || typeof value !== "object") return false;
    return Object.values(value as Record<string, unknown>).some((entry) => this.hasText(entry));
  }

  private hasText(value: unknown): boolean {
    return typeof value === "string" && value.trim().length > 0;
  }

  private normalizeOptionalString(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    return value.trim() || undefined;
  }
}
